import React, { useCallback, useEffect, useState } from "react";
import { Wallet, RotateCcw, Loader2 } from "lucide-react";
import { toast } from "sonner";
import Navbar from "../components/Navbar";
import { Button } from "../components/ui/button";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
  AlertDialogTrigger,
} from "../components/ui/alert-dialog";
import api from "../lib/api";
import { getPref, setPref } from "../lib/prefs";
import BenchmarkAchievements from "../components/BenchmarkAchievements";
import PortfolioStats from "../components/paper/PortfolioStats";
import EquityCurveCard from "../components/paper/EquityCurveCard";
import PositionsTable from "../components/paper/PositionsTable";
import TradesHistory from "../components/paper/TradesHistory";

const BENCHMARKS = [
  { symbol: "SPY", label: "S&P 500", asset_type: "etf" },
  { symbol: "QQQ", label: "Nasdaq 100", asset_type: "etf" },
  { symbol: "IWM", label: "Russell 2000 (small-cap)", asset_type: "etf" },
  { symbol: "VTI", label: "Total US Market", asset_type: "etf" },
  { symbol: "VT", label: "Global Stocks", asset_type: "etf" },
  { symbol: "DIA", label: "Dow Jones", asset_type: "etf" },
  { symbol: "GLD", label: "Gold", asset_type: "etf" },
  { symbol: "BTC", label: "Bitcoin", asset_type: "crypto" },
];

/**
 * Paper Trading page — overview of the user's virtual portfolio.
 *
 * Thin orchestrator: owns data-fetching state (portfolio / trades / history /
 * benchmark) and delegates rendering to four focused subcomponents in
 * `components/paper/*`. Was 401 lines as one file; now 105.
 */
export default function PaperTrading() {
  const [portfolio, setPortfolio] = useState(null);
  const [trades, setTrades] = useState([]);
  const [history, setHistory] = useState([]);
  const [benchmark, setBenchmark] = useState(null);
  const [showBenchmark, setShowBenchmark] = useState(true);
  const [benchmarkSymbol, setBenchmarkSymbol] = useState(() => getPref("benchmark") || "SPY");
  const [loading, setLoading] = useState(true);
  const [resetting, setResetting] = useState(false);

  const loadBenchmark = (sym) => {
    const at = BENCHMARKS.find((b) => b.symbol === sym)?.asset_type || "etf";
    api
      .get(`/paper/benchmark?symbol=${sym}&asset_type=${at}`)
      .then((r) => setBenchmark(r.data))
      .catch(() => setBenchmark(null));
  };

  const load = useCallback(() => {
    setLoading(true);
    const at = BENCHMARKS.find((b) => b.symbol === benchmarkSymbol)?.asset_type || "etf";
    Promise.all([
      api.get("/paper/portfolio"),
      api.get("/paper/trades?limit=30"),
      api.get("/paper/history"),
      api.get(`/paper/benchmark?symbol=${benchmarkSymbol}&asset_type=${at}`).catch(() => ({ data: null })),
    ])
      .then(([p, t, h, b]) => {
        setPortfolio(p.data);
        setTrades(t.data.trades || []);
        setHistory(h.data.snapshots || []);
        setBenchmark(b.data || null);
      })
      .finally(() => setLoading(false));
  }, [benchmarkSymbol]);

  const changeBenchmark = (sym) => {
    setBenchmarkSymbol(sym);
    setPref("benchmark", sym);
    setShowBenchmark(true);
    loadBenchmark(sym);
  };

  useEffect(() => { load(); }, [load]);

  const resetPortfolio = async () => {
    setResetting(true);
    try {
      await api.post("/paper/reset");
      toast.success("Portfolio reset to $10,000");
      load();
    } catch {
      toast.error("Reset failed");
    } finally {
      setResetting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F9F8F6]">
      <Navbar />
      <main className="max-w-6xl mx-auto px-6 md:px-8 lg:px-12 py-10 md:py-14" data-testid="paper-trading-page">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-10 animate-fade-up">
          <div>
            <div className="text-[10px] uppercase tracking-[0.25em] font-semibold text-[#6B7068]">
              Risk-free practice
            </div>
            <h1 className="font-heading text-4xl sm:text-5xl tracking-tighter font-medium text-[#2A2F2B] mt-1 flex items-center gap-3">
              Paper trading
              <Wallet className="w-7 h-7 text-[#D18E63]" strokeWidth={1.5} />
            </h1>
            <p className="text-[#6B7068] mt-2">Learn to trade with virtual cash. No risk, just reps.</p>
          </div>
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button
                variant="outline"
                data-testid="paper-reset-btn"
                className="rounded-full border-[#EAE6E1] text-[#6B7068] hover:text-[#C66B59] hover:border-[#C66B59]"
              >
                <RotateCcw className="w-4 h-4 mr-2" strokeWidth={1.5} /> Reset portfolio
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent className="rounded-2xl">
              <AlertDialogHeader>
                <AlertDialogTitle>Reset to $10,000?</AlertDialogTitle>
                <AlertDialogDescription>
                  This will clear all positions and trade history and restore your cash to $10,000.
                  This cannot be undone.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel className="rounded-full">Cancel</AlertDialogCancel>
                <AlertDialogAction
                  onClick={resetPortfolio}
                  disabled={resetting}
                  data-testid="paper-reset-confirm"
                  className="rounded-full bg-[#C66B59] hover:bg-[#a85748]"
                >
                  Reset
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>

        {loading ? (
          <div className="flex items-center gap-2 text-[#6B7068]">
            <Loader2 className="w-5 h-5 animate-spin" /> Loading...
          </div>
        ) : (
          <>
            <PortfolioStats portfolio={portfolio} />
            <EquityCurveCard
              portfolio={portfolio}
              history={history}
              benchmark={benchmark}
              showBenchmark={showBenchmark}
              setShowBenchmark={setShowBenchmark}
              benchmarks={BENCHMARKS}
              benchmarkSymbol={benchmarkSymbol}
              changeBenchmark={changeBenchmark}
            />
            <section className="mb-10" data-testid="achievements-section">
              <BenchmarkAchievements
                symbol={benchmarkSymbol}
                assetType={BENCHMARKS.find((b) => b.symbol === benchmarkSymbol)?.asset_type || "etf"}
              />
            </section>
            <PositionsTable positions={portfolio?.positions || []} />
            <TradesHistory trades={trades} />
          </>
        )}
      </main>
    </div>
  );
}
